import { Auth0PalmettoFinanceUser } from '../types/Organizations';

import { api } from './api';

interface AddUserParams {
  organizationAlias: string;
  email: string;
  permissions: string[];
  machine?: boolean;
  givenName?: string;
  familyName?: string;
  phoneNumber?: string;
}

interface AddUserResult {
  organization: string;
  error: { status: number; data: { message: string } } | null;
}

interface CurrentUserUpdates {
  givenName?: string;
  familyName?: string;
  name?: string;
  phoneNumber?: string | null;
  userIsSalesRep?: boolean;
}

export const userApi = api.injectEndpoints({
  endpoints: (build) => ({
    addUser: build.mutation<AddUserResult, AddUserParams>({
      query: ({ organizationAlias, email, permissions, machine = false, givenName, familyName, phoneNumber }) => ({
        url: `users/invite`,
        method: 'POST',
        body: { organizationAlias, email, permissions, machine, givenName, familyName, phoneNumber },
      }),
      invalidatesTags: (_result, _error, options) => {
        return [
          { type: 'Users', id: 'LIST' },
          { type: 'Organizations', id: options.organizationAlias },
        ];
      },
    }),
    editUser: build.mutation<
      Auth0PalmettoFinanceUser,
      {
        organizationAlias: string;
        userId: string;
        permissions: string[];
        givenName?: string;
        familyName?: string;
        phoneNumber?: string | null;
      }
    >({
      query: ({ userId, permissions, givenName, familyName, phoneNumber }) => ({
        url: `users/${userId}`,
        method: 'POST',
        body: { permissions, givenName, familyName, phoneNumber },
      }),
      invalidatesTags: (_result, _error, options) => {
        return [
          { type: 'Users', id: 'LIST' },
          { type: 'Organizations', id: options.organizationAlias },
        ];
      },
    }),
    removeUser: build.mutation<void, { organizationAlias: string; userId: string }>({
      query: ({ organizationAlias, userId }) => ({
        url: `users/${userId}`,
        method: 'DELETE',
        body: { organizationAlias },
      }),
      invalidatesTags: (_result, _error, options) => {
        return [
          { type: 'Users', id: 'LIST' },
          { type: 'Organizations', id: options.organizationAlias },
        ];
      },
    }),
    getPermissions: build.query<any, void>({
      query: () => {
        return {
          url: `users/permissions`,
        };
      },
    }),
    getCurrentUser: build.query<any, void>({
      query: () => {
        return {
          url: `users/current`,
        };
      },
      providesTags: () => [{ type: 'Users', id: 'CURRENT' }],
    }),
    editCurrentUser: build.mutation<void, CurrentUserUpdates>({
      query: (updates) => ({
        url: 'users/current',
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: (_result, _error) => {
        return [{ type: 'Users', id: 'CURRENT' }];
      },
    }),
  }),
});

export const {
  useAddUserMutation,
  useGetCurrentUserQuery,
  useLazyGetCurrentUserQuery,
  useGetPermissionsQuery,
  useRemoveUserMutation,
  useEditCurrentUserMutation,
  useEditUserMutation,
} = userApi;
